import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ShButtonComponent,
  ShMenuButtonComponent,
  ShMenuComponent,
} from '@seahorse/components';
import type { ActionMenuItem } from '@seahorse/ui';
import { PageAction } from '../page-header/page-header.models';

/**
 * Renders one primary action, a row of secondary actions and an overflow
 * menu, in the order the design handoff defines for tier 1 of `sh-page-header`
 * ("Step 4, streamlined", section 2): secondary actions, one primary action,
 * a divider, then the overflow `sh-menu-button`. `sh-peek-panel`'s footer
 * uses the same action model and reuses this component rather than
 * duplicating the rendering (design handoff, "Step 6, streamlined", section 1).
 *
 * No styles of its own: the host is a plain flex row wherever the caller's
 * own class (`.page-actions`, `.peek-footer-actions`, ...) supplies
 * `display: flex` -- see @seahorse/theme's `_page-header.scss`/`_peek.scss`.
 */
@Component({
  selector: 'sh-action-bar',
  // Default change detection, not OnPush: actions carry `visible` /
  // `disabled` as functions over page state (e.g. `() => this.peekLoading`),
  // which change without any input changing. Under OnPush a Recalculate
  // button stayed disabled after loading finished.
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    TranslateModule,
    ShButtonComponent,
    ShMenuComponent,
    ShMenuButtonComponent,
  ],
  templateUrl: './action-bar.component.html',
})
export class ActionBarComponent {
  primaryAction = input<PageAction>();
  secondaryActions = input<PageAction[]>();
  overflowActions = input<ActionMenuItem[]>();

  protected readonly translate = inject(TranslateService);

  // Only when at least one overflow item is currently visible: otherwise the
  // ⋮ would open an empty menu (e.g. a new record, where Delete is hidden).
  protected hasOverflow(): boolean {
    return (this.overflowActions() ?? []).some((item) =>
      typeof item.visible === 'function'
        ? item.visible(undefined)
        : item.visible !== false
    );
  }

  protected isActionDisabled(action: ActionMenuItem<void>): boolean {
    return typeof action.disabled === 'function'
      ? action.disabled(undefined)
      : !!action.disabled;
  }

  protected actionLabel(action: ActionMenuItem<void>): string {
    const label =
      typeof action.label === 'function'
        ? action.label(undefined)
        : action.label;
    return this.translate.instant(label);
  }

  protected runAction(action: ActionMenuItem<void>): void {
    if (this.isActionDisabled(action)) return;
    action.command(undefined);
  }
}
